"use client";

import * as React from "react";

import { createPortal } from "react-dom";

import { useKeyboardShortcut } from "@/hooks/use-keyboard-shortcut";
import { cn } from "@/utils/utils";

// Dialog context for open state and focus trap
type DialogContextType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
};
const DialogContext = React.createContext<DialogContextType | undefined>(undefined);

function useHideBackground(open: boolean) {
  React.useEffect(() => {
    if (!open) return;

    const root = document.body;
    const dialogRoot = document.getElementById("dialog-portal-root");
    const children = Array.from(root.children);
    for (const el of children) {
      if (dialogRoot?.contains(el)) continue;
      el.setAttribute("aria-hidden", "true");
      // @ts-expect-error  // inert is not a standard property, but used for focus management
      el.inert = true;
    }
    return () => {
      for (const el of children) {
        el.removeAttribute("aria-hidden");
        // @ts-expect-error  // inert is not a standard property, but used for focus management
        el.inert = false;
      }
    };
  }, [open]);
}

const Dialog = ({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}) => {
  // Internal state for uncontrolled usage
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  // Determine if controlled or uncontrolled
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange! : setInternalOpen;

  // Remove possible null from ref type
  const triggerRef = React.useRef<HTMLButtonElement>(null!);

  // Restore focus to trigger when dialog closes
  React.useEffect(() => {
    if (!open && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [open]);

  const contextValue = React.useMemo(() => ({ open, setOpen, triggerRef }), [open, setOpen, triggerRef]);

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
};

const DialogPortal = ({ children }: { children: React.ReactNode }) => {
  const ctx = React.useContext(DialogContext);
  // Use a dedicated portal root for dialog
  const [portalNode, setPortalNode] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    let node = document.getElementById("dialog-portal-root");
    if (!node) {
      node = document.createElement("div");
      node.id = "dialog-portal-root";
      document.body.appendChild(node);
    }
    setPortalNode(node);
  }, []);

  useHideBackground(!!ctx?.open);

  if (!portalNode) return null;
  return createPortal(children, portalNode);
};

const DialogTrigger = ({ children, asChild = false, ...props }: React.HTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) => {
  const ctx = React.useContext(DialogContext);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    ctx?.setOpen(true);
    props.onClick?.(e);
  };

  if (asChild) {
    // Clone the child element and merge our props
    const childElement = React.Children.only(children) as React.ReactElement<React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement> }>;

    return React.cloneElement(childElement, {
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        handleClick(e);
        // Call original onClick if it exists
        if ("onClick" in childElement.props && typeof childElement.props.onClick === "function") {
          childElement.props.onClick(e);
        }
      },
    });
  }

  return (
    <button ref={ctx?.triggerRef} {...props} onClick={handleClick}>
      {children}
    </button>
  );
};

const DialogOverlay = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
  const ctx = React.useContext(DialogContext);
  if (!ctx?.open) return null;
  return <div ref={ref} className={cn("fixed inset-0 z-50 bg-black/80", className)} onClick={() => ctx.setOpen(false)} tabIndex={-1} aria-hidden="true" {...props} />;
});
DialogOverlay.displayName = "DialogOverlay";

// Focus trap utility
function useFocusTrap(active: boolean, containerRef: React.RefObject<HTMLElement>) {
  React.useEffect(() => {
    if (!active || !containerRef.current) return;

    const node = containerRef.current;
    const focusableSelectors = [
      "a[href]",
      "area[href]",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "button:not([disabled])",
      "iframe",
      "object",
      "embed",
      '[tabindex]:not([tabindex="-1"])',
      "[contenteditable]",
    ];
    const getFocusable = () =>
      Array.from(node.querySelectorAll<HTMLElement>(focusableSelectors.join(","))).filter(
        (el) => !el.hasAttribute("disabled") && (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement)
      );

    // Focus the first focusable element
    const focusables = getFocusable();
    if (focusables.length) {
      focusables[0].focus();
    } else {
      node.focus();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const focusables = getFocusable();
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables.at(-1);

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    node.addEventListener("keydown", handleKeyDown);
    return () => {
      node.removeEventListener("keydown", handleKeyDown);
    };
  }, [active, containerRef]);
}

const DialogContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, children, ...props }, ref) => {
  const ctx = React.useContext(DialogContext);
  // Remove possible null from ref type
  const localRef = React.useRef<HTMLDivElement>(null!);

  // Merge forwarded ref and local ref
  React.useImperativeHandle(ref, () => localRef.current);

  // Focus trap only when open
  useFocusTrap(!!ctx?.open, localRef);

  // Handle ESC key to close dialog
  useKeyboardShortcut({ key: "Escape" }, () => {
    if (ctx?.open) {
      ctx.setOpen(false);
    }
  });

  if (!ctx?.open) return null;

  return (
    <DialogPortal>
      <DialogOverlay />
      <div
        ref={localRef}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 border bg-background p-6 shadow-lg sm:rounded-lg focus:outline-none",
          className
        )}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        data-testid="dialogContent"
        {...props}
      >
        {/* Close X button for Playwright and accessibility */}
        <button
          type="button"
          tabIndex={-1}
          aria-label="Close"
          title="Close"
          onClick={() => ctx?.setOpen(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        {children}
      </div>
    </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

const DialogClose = ({ children, ...props }: React.HTMLAttributes<HTMLButtonElement>) => {
  const ctx = React.useContext(DialogContext);
  return (
    <button
      {...props}
      onClick={(e) => {
        ctx?.setOpen(false);
        props.onClick?.(e);
      }}
    >
      {children}
    </button>
  );
};

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = "DialogDescription";

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };
